import type { User } from "@/contexts/UserContext";
import { useLazyBackendRequest } from "@/hooks/useBackendRequest";
import { useUser } from "@/hooks/useUser";
import { useLogto } from "@logto/react";
import { useEffect } from "react";

export function useAuth() {
  const { isAuthenticated, error, isLoading, signIn, signOut, getAccessToken } = useLogto();
  const { user, setUser } = useUser();
  const { request: fetchUser } = useLazyBackendRequest<User>(`/users/me`);

  async function login(redirectUrl?: string) {
    const callbackUrl = `${window.location.origin}/callback`;

    if (redirectUrl != null) {
      sessionStorage.setItem("redirectUrl", redirectUrl);
    }

    return signIn(callbackUrl);
  }

  async function logout() {
    await signOut(`${window.location.origin}`);
  }

  useEffect(() => {
    async function fetchUserData() {
      if (isAuthenticated && !user) {
        try {
          const response = await fetchUser();

          if (response.data) {
            setUser(response.data);
            return response.data;
          }

          console.error("Could not fetch user data");
          await logout();
          return null;
        } catch (error) {
          console.error("Error fetching user data:", error);
          return null;
        }
      } else if (!isAuthenticated) {
        setUser(null);
      }
    }
    fetchUserData();
  }, [isAuthenticated, user]);

  return {
    isAuthenticated,
    isLoading,
    error,
    user,
    login,
    logout,
    getAccessToken,
  };
}
